/* eslint-disable @typescript-eslint/no-explicit-any */
import "@material/web/button/filled-button";
import "@material/web/button/outlined-button";

import { CurrencyPipe, DecimalPipe, NgStyle } from "@angular/common";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  TemplateRef,
} from "@angular/core";
import { MatCardModule } from "@angular/material/card";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";

import { QPilotProduct } from "../types/product";
import { QPilotScheduledOrder } from "../types/scheduled-order";
import { QPilotSiteSettings } from "../types/site";
import { getCleanProductTitle } from "../../utils";

@Component({
  selector: "lib-add-product-card",
  standalone: true,
  imports: [DecimalPipe, MatCardModule, NgStyle, MatDialogModule, CurrencyPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./add-product-card.component.html",
  styleUrl: "./add-product-card.component.scss",
})
export class AddProductCardComponent implements OnInit, OnChanges {
  orderItemId: number;
  showQuantitySelector = false;
  productIsInOrder: boolean;
  quantity: number;
  quantityToAdd: number;
  isRecurring = true;
  dialogRef: MatDialogRef<any>;

  @Input() product: QPilotProduct;
  @Input() order: QPilotScheduledOrder;
  @Input() scheduledOrderVerboseName: string;
  @Input() siteSettings: QPilotSiteSettings;
  @Output() add = new EventEmitter();
  @Output() addOneTime = new EventEmitter();
  @Output() update = new EventEmitter();
  @Output() changeCycles = new EventEmitter();

  constructor(private dialog: MatDialog) {}

  ngOnInit() {
    this.orderItemId =
      this.order.scheduledOrderItems?.find(
        (item) => item.product?.id === this.product.id,
      )?.id ?? 0;

    // Apply image dimension settings if siteSettings is provided
    this.applyImageDimensionSettings();
  }

  /**
   * Applies the image dimension settings from site settings to CSS variables
   * Uses default values if settings are null or siteSettings is not provided
   */
  private applyImageDimensionSettings() {
    if (!this.siteSettings) return;

    const root = document.documentElement;
    const maxWidth = this.siteSettings?.styles?.maxImageWidthPx;
    const maxHeight = this.siteSettings?.styles?.maxImageHeightPx;

    // Set max width with default of 'auto' if null
    if (maxWidth) {
      root.style.setProperty("--product-max-width", `${maxWidth}px`);
    } else {
      root.style.setProperty("--product-max-width", "auto");
    }

    // Set max height with default of 200px if null
    if (maxHeight) {
      root.style.setProperty("--product-max-height", `${maxHeight}px`);
    } else {
      root.style.setProperty("--product-max-height", "200px");
    }
  }

  ngOnChanges() {
    const productInOrder = this.order.scheduledOrderItems?.find(
      (item) => item.product?.id === this.product.id,
    );

    this.productIsInOrder = productInOrder != null;
    this.quantity = productInOrder?.quantity ?? 1;
    this.quantityToAdd = this.quantity;
    this.isRecurring = productInOrder?.maxCycles !== 1;

    // Re-apply image dimension settings when inputs change
    this.applyImageDimensionSettings();
  }

  onAdd() {
    this.add.emit({
      orderId: this.order.id,
      quantityToAdd: this.quantityToAdd,
    });
  }

  onAddOneTime() {
    this.addOneTime.emit({
      orderId: this.order.id,
      quantityToAdd: this.quantityToAdd,
    });
  }

  onUpdate() {
    this.update.emit({
      orderId: this.order.id,
      orderItemId: this.orderItemId,
      quantityToUpdate: this.quantityToAdd,
    });
  }

  onChangeCycles() {
    this.changeCycles.emit({
      orderId: this.order.id,
      orderItemId: this.orderItemId,
      cycles: this.isRecurring ? "OneTime" : "Recurring",
    });
    this.dialogRef.close();
  }

  openQuantitySelector() {
    this.showQuantitySelector = true;
  }

  closeQuantitySelector() {
    this.showQuantitySelector = false;
  }

  decrementNewQuantityToAdd() {
    if (this.quantityToAdd <= 1) return;
    this.quantityToAdd--;
  }

  incrementNewQuantityToAdd() {
    this.quantityToAdd++;
  }

  openDialogWithRef(templateRef: TemplateRef<any>) {
    this.dialogRef = this.dialog.open(templateRef);
  }

  protected getProductImageUrl() {
    return (
      this.product?.imageUrl ??
      this.product?.metadata?.imageUrl ??
      this.product?.metadata?.imageThumbUrl ??
      ""
    );
  }

  getCleanProductTitle = getCleanProductTitle;
}
